import { useEffect, useRef, useState, type ReactNode, type RefObject, type MouseEvent as ReactMouseEvent } from 'react'
import { Activity, LayoutGrid, Maximize, Minimize, Monitor, PictureInPicture2, Volume2, VolumeX, X } from 'lucide-react'
import type { Track } from 'livekit-client'
import { useVoiceStore } from '@/store/voice'
import { cx } from '@/lib/cx'
import { toggleElementFullscreen } from '@/lib/fullscreen'
import { coarsePointer } from '@/lib/mobile'
import { getVideoStatsReport, readVideoStats, type StatsCursor } from '@/lib/videoStats'
import { qualityLabel, streamLabel } from '@/lib/streamView'
import { canPopOut, closePopOut, popOutStream, usePoppedOut } from './streamPip'
import { LiveBadge, StreamPlaceholder } from './StreamPreview'
import { StreamStats } from './StreamStats'
import { TrackVideo } from './TrackVideo'
import styles from './StreamStage.module.css'
import type { AvatarDecoration } from '@/types/api'

interface Props {
  userId: string
  /** Имя показывающего, как в списке участников. */
  name: string
  avatar?: string | undefined
  decoration?: AvatarDecoration | null | undefined
  color: string
  /** Глаз с числом зрителей (StreamViewers) — рядом с «В ЭФИРЕ». */
  viewers?: ReactNode
  channelName: string
  /** Название окна, если показывающий его прислал. */
  title: string
  own: boolean
  /** Дорожка экрана; пока подписка не доехала — undefined, на её месте заглушка. */
  track: Track | undefined
  /** Есть ли у трансляции звук. */
  audio: boolean
  /** Полоска участников снизу. */
  strip: ReactNode
  /** Кнопки голоса: микрофон, звук, камера, демонстрация, «отключиться». */
  controls: ReactNode
  onCollapse: () => void
  onClose: () => void
  /** Правый клик по картинке — меню человека с ползунком «Звук трансляции», а не меню видео браузера. */
  onContextMenu?: ((e: ReactMouseEvent) => void) | undefined
}

/** Сколько покоя до того, как шапка и кнопки спрячутся. */
const IDLE_MS = 2500
// пальцем дольше: панели показывают тапом, а потом ещё ищут глазами нужную кнопку
const TOUCH_IDLE_MS = 4000

/**
 * Развёрнутая трансляция, как в Discord: картинка на всё место, сверху канал и чей экран, справа
 * качество и «В ЭФИРЕ», снизу полоска участников и кнопки голоса, справа внизу — окно поверх
 * и весь экран. Шапка и кнопки прячутся, пока на них не смотрят.
 */
export function StreamStage({ userId, name, avatar, decoration, color, viewers, channelName, title, own, track, audio, strip, controls, onCollapse, onClose, onContextMenu }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const idle = useIdle(rootRef)
  const full = useIsFullscreen(rootRef)
  const quality = useStreamQuality(track)
  const popped = usePoppedOut() === userId
  const muted = useVoiceStore((s) => (s.prefs.streamVolumes[userId] ?? 1) === 0)
  const toggleStreamMute = useVoiceStore((s) => s.toggleStreamMute)
  const [stats, setStats] = useState(false)
  // отказ браузера или системы не должен выглядеть как кнопка, которая ничего не делает
  const [popError, setPopError] = useState<string | null>(null)
  const label = streamLabel(name)

  const popOut = () => {
    if (!track) return
    const video = rootRef.current?.querySelector('video')
    const aspect = video && video.videoHeight ? video.videoWidth / video.videoHeight : undefined
    const fail = (text: string) => {
      setPopError(text)
      setTimeout(() => setPopError(null), 4000)
    }
    setPopError(null)
    popOutStream(userId, track, label, aspect)
      .then((result) => {
        if (result === 'retry') fail('Окно поверх не открылось. Нажмите ещё раз')
      })
      .catch((err: unknown) => {
        if (err instanceof Error) console.warn('окно поверх не открылось:', err.message)
        fail('Окно поверх не открылось')
      })
  }

  const placeholder = (children?: ReactNode) => (
    <StreamPlaceholder name={name} title={title} avatar={avatar} decoration={decoration} color={color} size={96}>{children}</StreamPlaceholder>
  )

  return (
    <div ref={rootRef} className={styles.stage} data-idle={idle ? '' : undefined} role="region" aria-label={`Просмотр: ${label}`}>
      {/* двойной щелчок — весь экран, как в плеерах; пальцем два тапа — это показать и спрятать кнопки */}
      <div className={styles.picture} onDoubleClick={coarsePointer ? undefined : () => rootRef.current && toggleElementFullscreen(rootRef.current)} onContextMenu={onContextMenu}>
        {popped ? (
          placeholder(
            <button type="button" className={styles.back} onClick={closePopOut}>Вернуть сюда</button>,
          )
        ) : track ? (
          <TrackVideo track={track} className={styles.video} />
        ) : (
          placeholder()
        )}
      </div>

      <div className={styles.top} data-overlay="">
        <div className={styles.where}>
          <Volume2 size={18} className={styles.whereIcon} aria-hidden="true" />
          <span className={styles.channel}>{channelName}</span>
          <span className={styles.sep} aria-hidden="true" />
          <Monitor size={18} className={styles.whereIcon} aria-hidden="true" />
          <span className={styles.stream}>{label}</span>
          {title ? <span className={styles.window} data-tooltip={title}>{title}</span> : null}
        </div>
        <div className={styles.status}>
          {quality ? <span className={styles.quality}>{quality}</span> : null}
          {!audio ? (
            <span className={styles.silent} data-tooltip={own ? 'Вы показываете без звука' : 'Показывают без звука'}>
              <VolumeX size={16} aria-label={own ? 'Вы показываете без звука' : 'Показывают без звука'} />
            </span>
          ) : null}
          {viewers}
          <LiveBadge />
          <button type="button" className={styles.icon} data-tooltip="К плиткам" aria-label="К плиткам" onClick={onCollapse}>
            <LayoutGrid size={18} />
          </button>
          {!own ? (
            <button type="button" className={styles.icon} data-tooltip="Закрыть просмотр" aria-label="Закрыть просмотр" onClick={onClose}>
              <X size={20} />
            </button>
          ) : null}
        </div>
      </div>

      {stats && track ? (
        <div className={styles.stats}>
          <StreamStats track={track} own={own} />
        </div>
      ) : null}
      {popError ? <p className={styles.note} role="status">{popError}</p> : null}

      <div className={styles.bottom}>
        <div className={styles.strip} data-overlay="">{strip}</div>
        <div className={styles.bar} data-overlay="">
          <div className={styles.controls}>{controls}</div>
          <div className={styles.corner}>
            {!own && audio ? (
              <button
                type="button"
                className={cx(styles.icon, styles.big, muted && styles.off)}
                data-tooltip={muted ? 'Включить звук трансляции' : 'Выключить звук трансляции'}
                aria-label={muted ? 'Включить звук трансляции' : 'Выключить звук трансляции'}
                aria-pressed={muted}
                onClick={() => toggleStreamMute(userId)}
              >
                {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            ) : null}
            {track ? (
              <button type="button" className={cx(styles.icon, styles.big)} data-tooltip={stats ? 'Скрыть цифры трансляции' : 'Разрешение, кадры и битрейт'} aria-label="Цифры трансляции" aria-pressed={stats} onClick={() => setStats(!stats)}>
                <Activity size={20} />
              </button>
            ) : null}
            {canPopOut && track ? (
              <button type="button" className={cx(styles.icon, styles.big)} data-tooltip={popped ? 'Вернуть из окна' : 'Вынести в окно'} aria-label={popped ? 'Вернуть из окна' : 'Вынести в окно'} aria-pressed={popped} onClick={popped ? closePopOut : popOut}>
                <PictureInPicture2 size={20} />
              </button>
            ) : null}
            <button type="button" className={cx(styles.icon, styles.big)} data-tooltip={full ? 'Выйти из полноэкранного режима' : 'Во весь экран'} aria-label={full ? 'Выйти из полноэкранного режима' : 'Во весь экран'} onClick={() => rootRef.current && toggleElementFullscreen(rootRef.current)}>
              {full ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Прятать ли шапку и кнопки: после IDLE_MS покоя (пальцем — TOUCH_IDLE_MS) — да; движение мыши,
 * тап или клавиатура возвращают их. Пока курсор над кнопками или внутри них фокус с клавиатуры,
 * не прячем: человек целится или идёт по ним Tab'ом. Пальцем тап по картинке показывает и прячет
 * панели, как в плеере.
 */
function useIdle(ref: RefObject<HTMLElement | null>): boolean {
  const [idle, setIdle] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let timer: ReturnType<typeof setTimeout> | undefined
    let hidden = false
    let overControls = false
    let delay = IDLE_MS
    const hide = (value: boolean) => {
      if (hidden === value) return
      hidden = value
      setIdle(value)
    }
    const inOverlay = (t: EventTarget | null) => t instanceof Element && t.closest('[data-overlay]') !== null
    const busy = () => overControls || el.querySelector('[data-overlay] :focus-visible') !== null
    const schedule = () => {
      clearTimeout(timer)
      timer = setTimeout(() => (busy() ? schedule() : hide(true)), delay)
    }
    const wake = () => {
      hide(false)
      schedule()
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      delay = IDLE_MS
      overControls = inOverlay(e.target)
      wake()
    }
    const onDown = (e: PointerEvent) => {
      delay = e.pointerType === 'mouse' ? IDLE_MS : TOUCH_IDLE_MS
      if (e.pointerType === 'mouse' || inOverlay(e.target)) {
        wake()
        return
      }
      if (hidden) {
        wake()
        return
      }
      clearTimeout(timer)
      hide(true)
    }
    const onLeave = () => {
      overControls = false
    }
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointerleave', onLeave)
    el.addEventListener('keydown', wake)
    el.addEventListener('focusin', wake)
    schedule()
    return () => {
      clearTimeout(timer)
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointerleave', onLeave)
      el.removeEventListener('keydown', wake)
      el.removeEventListener('focusin', wake)
    }
  }, [ref])
  return idle
}

/** Развёрнут ли этот элемент на весь экран; при закрытии просмотра весь экран снимается. */
function useIsFullscreen(ref: RefObject<HTMLElement | null>): boolean {
  const [full, setFull] = useState(false)
  useEffect(() => {
    const el = ref.current
    const on = () => setFull(el !== null && document.fullscreenElement === el)
    document.addEventListener('fullscreenchange', on)
    return () => {
      document.removeEventListener('fullscreenchange', on)
      if (el && document.fullscreenElement === el) void document.exitFullscreen().catch(() => undefined)
    }
  }, [ref])
  return full
}

/** Качество, которое реально приходит: раз в две секунды из статистики WebRTC. */
function useStreamQuality(track: Track | undefined): string | null {
  const [label, setLabel] = useState<string | null>(null)
  useEffect(() => {
    if (!track) return
    let stop = false
    let cursor: StatsCursor | null = null
    const tick = async () => {
      const report = await getVideoStatsReport(track).catch(() => undefined)
      if (stop || !report) return
      const next = readVideoStats(report, cursor)
      if (!next) return
      cursor = next.cursor
      setLabel(qualityLabel(next.stats.height, next.stats.fps))
    }
    void tick()
    const timer = setInterval(() => void tick(), 2000)
    return () => {
      stop = true
      clearInterval(timer)
    }
  }, [track])
  return track ? label : null
}
